import { toast } from 'react-hot-toast'
import { findIndex } from 'lodash'
import { useMutation } from '@apollo/client'
import { userLessonUpsertOne } from '../../graphql'
import { CourseType, LessonType } from '../../types'
import { getCourseLessons, useSegment } from '.'

export const useLesson = () => {
  const [UserLessonUpsertOne] = useMutation(userLessonUpsertOne)
  const { track } = useSegment()

  // Returns the next lesson after the provided lesson in the course
  const getNextLesson = (_id: string, course: CourseType) => {
    const lessons = getCourseLessons(course)
    const index = findIndex(lessons, (l) => l._id === _id)

    return lessons[index + 1]
  }

  // Marks the designated lesson as complete
  const handleComplete = async (lesson: LessonType) => {
    try {
      const eventName = lesson.isCompleted ? 'Uncompleted Lesson' : 'Completed Lesson'

      track(eventName, {
        _id: lesson._id,
        title: lesson.title,
        slug: lesson.slug,
        video_duration: lesson?.video?.duration || 0,
        file_count: lesson?.additionalFiles?.length || 0,
        module_id: lesson.module,
        course_id: lesson.course
      })

      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isCompleted: !lesson.isCompleted,
          isSkipped: false
        }
      })
    } catch (err) {
      toast.error((err as any).message)
    }
  }

  // If the designated lesson is not completed and not skipped,
  // it marks it as skipped
  const handleSkip = async (lesson: LessonType) => {
    try {
      // If the lesson is already skipped, or is completed
      // don't bother making the GraphQL call
      if (lesson.isSkipped || lesson.isCompleted) {
        return
      }

      track('Skipped Lesson', {
        _id: lesson._id,
        title: lesson.title,
        slug: lesson.slug,
        video_duration: lesson?.video?.duration || 0,
        file_count: lesson?.additionalFiles?.length || 0,
        module_id: lesson.module,
        course_id: lesson.course
      })

      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isSkipped: true
        }
      })
    } catch (err) {
      toast.error((err as any).message)
    }
  }

  // Unskips the designated lesson
  const handleUnSkip = async (lesson: LessonType) => {
    try {
      if (!lesson.isSkipped) {
        return
      }

      track('Unskipped Lesson', {
        _id: lesson._id,
        title: lesson.title,
        slug: lesson.slug,
        video_duration: lesson?.video?.duration || 0,
        file_count: lesson?.additionalFiles?.length || 0,
        module_id: lesson.module,
        course_id: lesson.course
      })

      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isSkipped: false
        }
      })
    } catch (err) {
      toast.error((err as any).message)
    }
  }

  return {
    getNextLesson,
    handleComplete,
    handleSkip,
    handleUnSkip
  }
}
